import React, { useState, useEffect } from "react";
import "@/App.css";
import Navigation from "./components/Navigation";
import HeroSection from "./components/HeroSection";
import UpcomingProjectsSection from "./components/UpcomingProjectsSection";
import AboutSection from "./components/AboutSection";
import WalksSection from "./components/WalksSection";
import GamesSection from "./components/GamesSection";
import WorkshopsSection from "./components/WorkshopsSection";
import ContactButton from "./components/ContactButton";
import Footer from "./components/Footer";
import { getAllContent } from "./services/api";

// Fallback mock data in case API fails
import { heroData as mockHero, upcomingProjectsData as mockUpcomingProjects, aboutData as mockAbout, walksData as mockWalks, gamesData as mockGames, workshopsData as mockWorkshops, contactData as mockContact } from "./data/mock";

const MN_RAO_DESCRIPTION =
  "MN Krishnarao Park resides in the heart of Basavanagudi and is an intrinsic part of the lives of the people who live there. We explored the history of the area and the life of the man it was named after amongst other things.";

const SANKEY_TANK_DESCRIPTION =
  "We tried to understand the layered history of Bengaluru's water systems while walking around Sankey Tank. This walk was not only about the heritage of the city but also about how we imagine the future of water.";

const SANKEY_TANK_IMAGE_URLS = [
  "/walks/sankey/sankey-01.jpeg",
  "/walks/sankey/sankey-02.jpeg",
  "/walks/sankey/sankey-03.jpeg",
  "/walks/sankey/sankey-04.jpeg"
];

const GANDHI_BAZAAR_IMAGE_URLS = [
  "/walks/gandhi-bazaar/gandhi-01.jpeg",
  "/walks/gandhi-bazaar/gandhi-02.jpeg",
  "/walks/gandhi-bazaar/gandhi-03.jpeg",
  "/walks/gandhi-bazaar/gandhi-04.jpeg",
  "/walks/gandhi-bazaar/gandhi-05.jpeg"
];
const GANDHI_BAZAAR_DESCRIPTION =
  "We took an experimental mapping walk around Gandhi Bazaar to map the movement and life of the market. Our goal was to understand how people use spaces and whether we can truly design for the chaos of a bustling Indian market.";

const MN_RAO_IMAGE_URLS = [
  "/walks/mn-rao/mn-rao-01.jpg",
  "/walks/mn-rao/mn-rao-02.jpg",
  "/walks/mn-rao/mn-rao-03.jpg",
  "/walks/mn-rao/mn-rao-04.jpg",
  "/walks/mn-rao/mn-rao-05.jpg"
];

const COLES_PARK_POINT = {
  id: 4,
  name: "Coles Park",
  title: "A Walk in the Park - Coles Park",
  subtitle: "a walk by women for women",
  description:
    "We wanted to imagine what a public park would look like if it were designed by women. So, we invited women to come join us at Coles Park to walk, observe, and imagine a future where spaces are designed by us. We discussed safety, sanitation, love, and how we can incorporate infrastructure that can take off the burden to being the primary care giver.",
  imageUrl: "/walks/women-walk/a-walk-in-the-park-main.jpeg",
  imageUrls: [
    "/walks/women-walk/a-walk-in-the-park-main.jpeg",
    "/walks/women-walk/coles-park-01.jpeg",
    "/walks/women-walk/coles-park-02.jpeg",
    "/walks/women-walk/coles-park-03.jpeg"
  ],
  imageFit: "contain",
  position: { x: 50, y: 82 }
};
const BASAVANAGUDI_STORIES_POINT = {
  id: 5,
  name: "A Walk by the Lake",
  title: "A Walk by the Lake",
  subtitle: "In collaboration with Mobility Champions from YLAC",
  description:
    "Recently, we took a walk by Sankey Tank to understand the heritage of the tank, explore the present challenges to public spaces, and imagine the future of the water systems in the city. The idea was to understand the layered history of the space, tracing how the tank evolved and sustained its environs, and the threats that the water system faces now.\n\nThe Mobility Champions, who are currently working on different projects across the city to make it more accessible, guided the participants through activities that expanded and challenged the ideas we have about mobility and accessibility in public spaces. How do we audit public spaces and footpaths? What avenues can we as citizens take to be active stakeholders? How do we design things like bus stops to be more accessible to a diverse demography? How can we design footpaths for everyone, including informal workers? Together, we tried to understand these questions and find solutions!",
  imageUrl: "/walks/walk-by-the-lake/FIMO_1776483861143.jpg",
  imageUrls: [
    "/walks/walk-by-the-lake/FIMO_1776483861143.jpg",
    "/walks/walk-by-the-lake/FIMO_1776484844345.jpg",
    "/walks/walk-by-the-lake/IMG_20260418_094458259.jpg",
    "/walks/walk-by-the-lake/FIMO_1776484829970.jpg"
  ],
  position: { x: 22, y: 42 }
};

const applyWalksOverrides = (sourceWalks) => {
  if (!sourceWalks || !Array.isArray(sourceWalks.locations)) {
    return sourceWalks;
  }

  const enrichedLocations = sourceWalks.locations.map((location) => {
    const nameOrTitle = `${location?.name || ""} ${location?.title || ""}`;
    const isSankey =
      typeof location?.name === "string" &&
      /sankey\s*tank/i.test(location.name);
    const isGandhiBazaar =
      typeof location?.name === "string" &&
      /gandhi\s*bazaar/i.test(location.name);
    const isMnRao =
      typeof location?.name === "string" &&
      /mn\s*krishna?\s*rao/i.test(location.name);
    const isWalkByLake =
      /basavanagudi\s*stories|walk\s*by\s*the\s*lake/i.test(nameOrTitle);

    if (isWalkByLake) {
      return {
        ...location,
        ...BASAVANAGUDI_STORIES_POINT,
        id: location.id ?? BASAVANAGUDI_STORIES_POINT.id,
        position: location.position || BASAVANAGUDI_STORIES_POINT.position
      };
    }

    if (isSankey) {
      return {
        ...location,
        description: SANKEY_TANK_DESCRIPTION,
        imageUrl: SANKEY_TANK_IMAGE_URLS[0],
        imageUrls: SANKEY_TANK_IMAGE_URLS
      };
    }

    if (isGandhiBazaar) {
      return {
        ...location,
        description: GANDHI_BAZAAR_DESCRIPTION,
        imageUrl: GANDHI_BAZAAR_IMAGE_URLS[0],
        imageUrls: GANDHI_BAZAAR_IMAGE_URLS
      };
    }

    if (!isMnRao) {
      return location;
    }

    return {
      ...location,
      description: MN_RAO_DESCRIPTION,
      imageUrl: MN_RAO_IMAGE_URLS[0],
      imageUrls: MN_RAO_IMAGE_URLS
    };
  });

  const hasColesPark = enrichedLocations.some(
    (location) => typeof location?.name === "string" && /coles\s*park/i.test(location.name)
  );
  const hasWalkByLakePoint = enrichedLocations.some(
    (location) =>
      /basavanagudi\s*stories|walk\s*by\s*the\s*lake/i.test(
        `${location?.name || ""} ${location?.title || ""}`
      )
  );

  const usedIds = new Set(
    enrichedLocations
      .map((location) => Number(location?.id))
      .filter((id) => Number.isInteger(id))
  );
  const getNextId = (preferredId) => {
    if (!usedIds.has(preferredId)) {
      usedIds.add(preferredId);
      return preferredId;
    }

    let candidate = 1;
    while (usedIds.has(candidate)) {
      candidate += 1;
    }

    usedIds.add(candidate);
    return candidate;
  };

  const appendedLocations = [];
  if (!hasColesPark) {
    appendedLocations.push({ ...COLES_PARK_POINT, id: getNextId(COLES_PARK_POINT.id) });
  }
  if (!hasWalkByLakePoint) {
    appendedLocations.push({
      ...BASAVANAGUDI_STORIES_POINT,
      id: getNextId(BASAVANAGUDI_STORIES_POINT.id)
    });
  }

  return {
    ...sourceWalks,
    locations: [...enrichedLocations, ...appendedLocations]
  };
};

function App() {
  const [content, setContent] = useState(null);

  useEffect(() => {
    const fetchContent = async () => {
      try {
        const data = await getAllContent();
        setContent({
          ...data,
          walks: applyWalksOverrides(data?.walks)
        });
      } catch (err) {
        console.warn("API unavailable, using mock data:", err.message);
        // Fallback to mock data
        setContent({
          hero: mockHero,
          upcomingProjects: mockUpcomingProjects,
          about: mockAbout,
          walks: applyWalksOverrides(mockWalks),
          games: mockGames,
          workshops: mockWorkshops,
          contact: mockContact
        });
      }
    };

    fetchContent();
  }, []);

  return (
    <div className="App">
      <Navigation />
      <HeroSection data={content?.hero} gameData={content?.games} />
      <UpcomingProjectsSection
        data={[content?.upcomingProjects || mockUpcomingProjects]}
      />
      <AboutSection data={content?.about} />
      <WalksSection data={content?.walks} />
      <GamesSection data={content?.games} />
      <WorkshopsSection data={content?.workshops} />
      <ContactButton data={content?.contact} />
      <Footer />
    </div>
  );
}

export default App;
